import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';
import TollRoundedIcon from '@mui/icons-material/TollRounded';
import { DashboardSectionHeader } from '@/shared/components/dashboard/DashboardSectionHeader';
import { dashboardPanelSx } from '@/shared/styles/surfaces';
import { useCreditTransactions } from '../hooks/useGrowth';
import type { CreditsWallet } from '../types';
import { RewardChip } from './RewardChip';

const n = (value?: number) => (value ?? 0).toLocaleString('en-IN');

export function CreditsWalletSection({ wallet, loading }: { wallet?: CreditsWallet; loading: boolean }) {
  const { t } = useTranslation('rewards');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const transactions = useCreditTransactions(page + 1, limit);

  const tiles = [
    { label: t('credits.pending'), value: wallet?.pending },
    { label: t('credits.lifetimeEarned'), value: wallet?.lifetimeEarned },
    ...(wallet?.lifetimeRedeemed ? [{ label: t('credits.lifetimeRedeemed'), value: wallet.lifetimeRedeemed }] : []),
  ];

  return (
    <Box id="credits" sx={{ scrollMarginTop: 88 }}>
      <DashboardSectionHeader title={t('credits.title')} subtitle={t('credits.subtitle')} />
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '340px 1fr' }, alignItems: 'start' }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 'var(--radius-lg)',
            p: { xs: 2.5, sm: 3 },
            color: '#FFFFFF',
            background:
              'radial-gradient(circle at 100% 0%, rgba(200,169,110,0.22) 0%, transparent 55%), linear-gradient(150deg, #1A2E1A 0%, #2F4B2F 100%)',
            boxShadow: 'var(--shadow-md)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              aria-hidden
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                background: 'linear-gradient(135deg, #E9D7AE 0%, #C8A96E 100%)',
                color: '#1A2E1A',
              }}
            >
              <TollRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.12em', color: '#E9D7AE' }}>
              {t('credits.available').toUpperCase()}
            </Typography>
          </Box>

          {loading ? (
            <Skeleton width={150} height={52} sx={{ mt: 1.5, bgcolor: 'rgba(255,255,255,0.12)' }} />
          ) : (
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1 }}>
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: '2.25rem', sm: '2.5rem' },
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.1,
                  fontVariantNumeric: 'tabular-nums',
                  color: '#FFFFFF',
                }}
              >
                {n(wallet?.available)}
              </Typography>
              <Typography component="span" sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#FFFFFF' }}>
                {t('common.credits')}
              </Typography>
            </Box>
          )}

          <Box
            component="dl"
            sx={{ m: 0, mt: 2.5, pt: 1, borderTop: '1px solid rgba(233,215,174,0.25)', display: 'grid' }}
          >
            {tiles.map((tile, index) => (
              <Box
                key={tile.label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 2,
                  py: 1,
                  borderTop: index ? '1px solid rgba(255,255,255,0.08)' : 'none',
                }}
              >
                <Typography component="dt" sx={{ fontSize: '0.85rem', color: '#FFFFFF', fontWeight: 600 }}>
                  {tile.label}
                </Typography>
                <Typography
                  component="dd"
                  sx={{ m: 0, fontWeight: 800, fontSize: '1rem', fontVariantNumeric: 'tabular-nums', color: '#FFFFFF' }}
                >
                  {loading ? '—' : n(tile.value)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ ...dashboardPanelSx, overflow: 'hidden' }}>
          <Typography fontWeight={800} sx={{ px: 2.5, pt: 2, pb: 1 }}>
            {t('credits.history')}
          </Typography>
          {transactions.isError ? (
            <Alert severity="error" sx={{ m: 2 }}>{t('common.error')}</Alert>
          ) : transactions.isLoading ? (
            <Box sx={{ p: 2, display: 'grid', gap: 1 }}>
              {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={48} />)}
            </Box>
          ) : !transactions.data?.items.length ? (
            <Typography color="text.secondary" sx={{ px: 2.5, py: 4, textAlign: 'center' }}>
              {t('credits.empty')}
            </Typography>
          ) : (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {transactions.data.items.map((tx) => (
                <Box
                  component="li"
                  key={tx._id}
                  sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, borderTop: '1px solid', borderColor: 'divider' }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={700} fontSize="0.92rem" noWrap title={tx.description}>
                      {tx.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.25, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary">
                        {t(`credits.sources.${tx.source}`)} · {dayjs(tx.createdAt).format('D MMM YYYY, h:mm A')}
                      </Typography>
                      <RewardChip
                        label={t(`credits.types.${tx.type}`)}
                        tone={tx.type === 'CREDIT' ? 'success' : tx.type === 'REVERSAL' ? 'error' : 'neutral'}
                      />
                    </Box>
                  </Box>
                  <Typography sx={{ fontWeight: 800, color: tx.amount >= 0 ? 'success.dark' : 'error.main', whiteSpace: 'nowrap' }}>
                    {tx.amount >= 0 ? '+' : '−'}
                    {n(Math.abs(tx.amount))}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          {Boolean(transactions.data?.total) && (
            <TablePagination
              component="div"
              count={transactions.data?.total ?? 0}
              page={page}
              onPageChange={(_e, next) => setPage(next)}
              rowsPerPage={limit}
              onRowsPerPageChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25]}
            />
          )}
        </Paper>
      </Box>
    </Box>
  );
}
